import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Row, Col, Label,
} from 'reactstrap';
import { Editor, Image } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, ContentState, Modifier, SelectionState } from 'draft-js';
import 'draft-js/dist/Draft.css';

import './textEditor.scss';

import htmlToDraft from 'html-to-draftjs';
import { sendLanguageType } from '../../../constants/constants';
import ImageBlock from './ImageBlock';
import { imageEntityTransform } from '../../../utils/draftUtils';

const TextEditor = ({ editorState, editorStateSet, languages, handleEditState, readOnly, originalPadding, viewEditorState, viewLang, toggleEditorSet, chooseFile, addEditorClassName, notChangedContent, disableColorPicker }) => {
  const { t } = useTranslation('admin');

  let readEditor = null;
  if (readOnly && viewEditorState) {
    // const blocksFromHtml = htmlToDraft(viewEditorState);
    const blocksFromHtml = htmlToDraft(viewEditorState, imageEntityTransform);

    
    const { contentBlocks, entityMap } = blocksFromHtml;
    
    const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

    if (contentState) {
      readEditor = EditorState.createWithContent(contentState);
    } else {
      readEditor = EditorState.createEmpty();
    }
  }

  if (!editorStateSet && !readOnly) {
    return null;
  }

  const uploadCallback = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        resolve({
          data: {
            link: reader.result, // base64 image
          },
        });
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };


  const editorClassName = `${!originalPadding && 'px-0'} ${readOnly && 'editor-readOnly'}`;

  const editorOptions = [
    "inline",
    "blockType",
    "fontSize",
    "fontFamily",
    "list",
    "textAlign",
    ...(disableColorPicker ? [] : ["colorPicker"]),
    "link",
    "emoji",
    "image",
    "remove",
    "history",
  ];

  const colorPickerColors = [
    "#000000",
    "#FFFFFF",
    "#808080",
    "#C0C0C0",

    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FFFF00",

    "#FFA500",
    "#800080",
    "#00FFFF",
    "#FF00FF",
  ];

  
  const blockRendererFn = (editorState, onEditorStateChange, readOnly) => (
    block
  ) => {
    if (block.getType() === "atomic") {
      return {
        component: ImageBlock,
        editable: false,
        props: {
          editorState,
          onEditorStateChange,
          readOnly,
        },
      };
    }
  };

  return (
    <div>
      {
        !readOnly ? (
          languages && !notChangedContent ? (
            languages.map(language => (
              <Row>
                <Col>
                  <Label>{sendLanguageType[language]}</Label>
                </Col>
                <Col xs="12" className={editorClassName}>
                  <Editor
                    className="content-management-text-area"
                    editorState={editorState[language] || {}}
                    toolbarClassName="toolbarClassName"
                    wrapperClassName="wrapperClassName"
                    editorClassName={`editorClassName ${addEditorClassName}`}
                    readOnly={readOnly}
                    onEditorStateChange={state => handleEditState(state, language)}
                    blockRendererFn={blockRendererFn(
                      editorState[language],
                      (state) => handleEditState(state, language),
                      readOnly
                    )}
                    toolbar={{
                      inputAccept: 'application/pdf,text/plain,application/vnd.openxmlformatsofficedocument.wordprocessingml.document,application/msword,application/vnd.ms-excel',
                      colorPicker: {
                        colors: colorPickerColors,
                      },
                      options: editorOptions,
                      image: {
                        uploadEnabled: true,
                        uploadCallback: chooseFile,
                        previewImage: true,
                        alt: { present: true, mandatory: false },
                        urlEnabled: true,
                        inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg'
                      },
                    }}
                  />
                </Col>
              </Row>
            ))
          ) : (
            <Editor
              className="content-management-text-area"
              editorState={editorState || {}}
              toolbarClassName="toolbarClassName"
              wrapperClassName="wrapperClassName"
              editorClassName={`editorClassName ${addEditorClassName}`}
              readOnly={readOnly}
              onEditorStateChange={state => handleEditState(state)}
              blockRendererFn={blockRendererFn(
                editorState,
                (state) => handleEditState(state),
                readOnly
              )}
              toolbar={{
                inputAccept: 'application/pdf,text/plain,application/vnd.openxmlformatsofficedocument.wordprocessingml.document,application/msword,application/vnd.ms-excel',
                colorPicker: {
                  colors: colorPickerColors
                },
                options: editorOptions,
                image: {
                  uploadEnabled: true,
                  uploadCallback: chooseFile,
                  previewImage: true,
                  alt: { present: true, mandatory: false },
                  urlEnabled: true,
                  inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg'
                },
              }}
            />
          )
        ) : (
          <Row>
            <Col xs="12" className={editorClassName}>
              <Editor
                className="content-management-text-area"
                editorState={readEditor || {}}
                toolbarClassName="toolbarClassName"
                wrapperClassName="wrapperClassName"
                editorClassName={`editorClassName ${addEditorClassName}`}
                toolbarHidden={readOnly}
                readOnly={readOnly}
              />
            </Col>
          </Row>
        )
      }
    </div>
  );
};

TextEditor.propTypes = {
  editorState: PropTypes.shape({}),
  editorStateSet: PropTypes.bool,
  languages: PropTypes.arrayOf(PropTypes.string),
  readOnly: PropTypes.bool,
  originalPadding: PropTypes.bool,
  handleEditState: PropTypes.func.isRequired,
  viewEditorState: PropTypes.string,
  viewLang: PropTypes.string,
  toggleEditorSet: PropTypes.func.isRequired,
  chooseFile: PropTypes.func.isRequired,
  addEditorClassName: PropTypes.string,
  notChangedContent: PropTypes.bool,
  disableColorPicker: PropTypes.bool,
};

TextEditor.defaultProps = {
  editorState: {},
  editorStateSet: false,
  languages: ['lv', 'en', 'ru'],
  readOnly: false,
  originalPadding: false,
  viewEditorState: null,
  viewLang: null,
  addEditorClassName: '',
  notChangedContent: false,
  disableColorPicker: false,
};

export default TextEditor;
